import React, { useEffect, useState } from 'react'
import { Loader2Icon } from 'lucide-react'

// Components
import EventFormFooterCommonComponent from '../../../common/buttons/EventFormFooterCommonComponent'
import EventFormHeaderComponent from '../header/EventFormHeaderComponent'
import { CustomSchedules } from './components/CustomSchedules'
import { EntriesSchedules } from './components/EntriesSchedules'
import EventSchedulingFormComponentNoteModal from './components/EventSchedulingFormComponentNoteModal'
import { Facilities } from './components/Facilities'
import CustomDatePicker from '../../../common/inputs/CustomDatePicker'
import { IconSearch } from '../../../icons/IconSearch'

// Third party
import { v4 as uuidv4 } from 'uuid'
import { yupResolver } from '@hookform/resolvers/yup'
import { AddCircleOutline } from '@mui/icons-material'
import { useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { Tooltip } from '@mui/material'
import Select from 'react-select'

import FirestoreService from '../../../../services/firestoreService'
import RegistrationDyDayService from '../../../../services/firebase-service/registration-by-day/RegistrationDyDayService'
import EventReviewPublishService from '../../../../services/firebase-service/event-review-publish/EventReviewPublishService'

import useGetEventData from '../../../../hooks/users/common/useGetEventData'

import { clearAllArr } from '../../../../commonHelpers/helpers'
import useToasterHelper from '../../../../helpers/ToasterHelper'
import { getConvertedData, getReactPickerDate } from '../../../../models/interface.helper'
import { daysOfWeek, sortScheduling } from '../../../../helpers/time'
import { cn } from '../../../../lib/utils'

import {
  IEventFees,
  RegistrationFeesType,
} from '../../../../models/event-fees/event-fees.interface'
import { EventFeesModel } from '../../../../models/event-fees/event-fees.model'
import { EventSchedulingModel } from '../../../../models/event-scheduling/event-scheduling.model'
import {
  IEventScheduleInterface,
  TCustomSchedule,
} from '../../../../models/event-scheduling/event-scheduling.interface'
import { IRegistrationByDayInterface } from '../../../../models/registrations-by-day/registrationByDay.interface'

import { CONST } from '../../../../const/const'
import { MODAL_CONSTS } from '../../../../const/modal-const'
import { MESSAGES_CONST } from '../../../../const/messages-const'
import { TAG_OPTIONS } from '../../../../fakeData/TimeList'

import { customSelectActiveStyles } from '../../../../customStyles/ReactSelectCustomStyle'

import './assets/scheduling.css'
import './SchedulingScreenEventSchedulingFormComponent.css'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

interface SchedulingScreenEventSchedulingFormComponentProps {
  onValid: any
  onInvalid: any
  onValidAndExit: any
  onInvalidAndExit: any
  handleModal: (showHide: boolean, typeOfModal: string, dataToPassOn?: any) => void
  nextEventTab?: string
  eventTab?: string
}

const eventSchedulingFormDefaultValues = new EventSchedulingModel().toObject()
const eventSchedulingFormValidationSchema = EventSchedulingModel.validationSchema()

const eventFeesFormDefaultValues = new EventFeesModel().toObject()
const validationSchemaSchedulingByClass = EventFeesModel.validationSchemaSchedulingByClass()
const validationSchemaSchedulingByRider = EventFeesModel.validationSchemaSchedulingByRider()

export interface IFilters {
  facility: string
  day: string
  classes: string
}
export interface IBatchValues {
  facility: string
  startDate: string
  tag: string
}

export const filteredSchedule = ({
  searchValue,
  filters,
  field,
  registrationByDay,
}: {
  searchValue: string
  filters: IFilters
  field: RegistrationFeesType | TCustomSchedule
  registrationByDay: IRegistrationByDayInterface[]
}) => {
  const countRiders = registrationByDay.filter((rider) => rider.uuid === field.uuid).length

  const filterByFacility = filters.facility.length <= 0 || filters.facility.includes(field.location)

  const filterByDay =
    filters.day.length <= 0 || filters.day.includes(daysOfWeek[new Date(field.startDate)?.getDay()])

  const filterByClassName = field.name?.toLowerCase()?.includes(searchValue.toLowerCase())

  const filterByEntries = filters.classes
    ? (filters.classes === 'Inactive classes' && countRiders <= 0) ||
      (filters.classes === 'Active classes' && countRiders > 0) ||
      filters.classes === 'All classes'
    : true

  return filterByFacility && filterByDay && filterByClassName && filterByEntries
}

export const filteredParentSchedule = ({
  searchValue,
  filters,
  field,
  registrationByDay,
}: {
  searchValue: string
  filters: IFilters
  field: RegistrationFeesType
  registrationByDay: IRegistrationByDayInterface[]
}) => {
  return field.children?.some((child) =>
    filteredSchedule({ searchValue, filters, field: child, registrationByDay })
  )
}

const classOptions = [
  { label: 'All classes', value: 'All classes' },
  { label: 'Active classes', value: 'Active classes' },
  { label: 'Inactive classes', value: 'Inactive classes' },
]

const SchedulingScreenEventSchedulingFormComponent: React.FC<
  SchedulingScreenEventSchedulingFormComponentProps
> = ({
  onValid,
  onValidAndExit,
  onInvalid,
  onInvalidAndExit,
  handleModal,
  eventTab,
  nextEventTab,
}) => {
  const { getAllData } = useGetEventData()
  const toastFunctions = useToasterHelper()

  const { eventId } = useParams<{ eventId: string }>()

  const [noteModalShow, setNoteModalShow] = useState(false)
  const [locationOptions, setLocationOptions] = useState<{ label: string; value: string }[]>([])
  const [filters, setFilters] = useState<IFilters>({
    facility: '',
    day: '',
    classes: '',
  })
  const [batchValues, setBatchValues] = useState<IBatchValues>({
    facility: '',
    startDate: '',
    tag: '',
  })
  const [searchValue, setSearchValue] = useState('')
  const [isBatchSchedule, setIsBatchSchedule] = useState(false)
  const [batchLoading, setBatchLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [registrationByDay, setRegistrationByDay] = useState<IRegistrationByDayInterface[]>([])

  const {
    reset: resetSchedule,
    watch: watchSchedule,
    control: controlSchedule,
    trigger: triggerSchedule,
    setValue: setValueSchedule,
    register: registerSchedule,
    getValues: getValuesSchedule,
    handleSubmit: handleSubmitSchedule,
    formState: { errors: errorsSchedule },
  } = useForm<IEventScheduleInterface>({
    defaultValues: { ...eventSchedulingFormDefaultValues },
    resolver: yupResolver(eventSchedulingFormValidationSchema),
    mode: 'onSubmit',
  })

  const facilities = useFieldArray({
    control: controlSchedule,
    name: 'facilities',
  })

  const { fields, append, remove, update } = useFieldArray({
    control: controlSchedule,
    name: 'custom',
  })

  const {
    reset: resetEntries,
    watch: watchEntries,
    trigger: triggerEntries,
    control: controlEntries,
    setValue: setValueEntries,
    register: registerEntries,
    getValues: getValuesEntries,
    handleSubmit: handleSubmitEntries,
    formState: { errors: errorsEntries },
  } = useForm<IEventFees>({
    defaultValues: { ...eventFeesFormDefaultValues },
    resolver: yupResolver(
      watchSchedule('scheduleBy') === 'class'
        ? validationSchemaSchedulingByClass
        : validationSchemaSchedulingByRider
    ),
    mode: 'onChange',
  })

  //! Getting all event data
  useEffect(() => {
    const getData = async () => {
      getAllData(eventId, ['v01_event_scheduling', 'v01_event_fees']).then()
      setRegistrationByDay(await RegistrationDyDayService.getByEventIds([eventId]))
    }
    if (eventId) getData().then()
  }, [eventId, getAllData])

  const custom = watchSchedule('custom')
  const registrationFees = watchEntries('registrationFees')
  // default to class when undefined to ensure backwards compatability
  const scheduleBy = watchSchedule('scheduleBy') ?? 'class'

  const handleSchedule = () => {
    const values = getConvertedData([
      ...getValuesSchedule('custom')!,
      ...getValuesEntries('registrationFees')!,
    ])

    handleModal(true, MODAL_CONSTS.VIEW_SCHEDULE_ORGANIZER, {
      data: values,
      scheduleBy,
    })
  }

  const daysOptions: { startDate: string | Date; day: string }[] = []
  ;[...(registrationFees ?? []), ...(custom ?? [])].forEach((fee) => {
    const day = daysOfWeek[new Date(fee.startDate)?.getDay()]
    if (!daysOptions.find((date) => date.day === day))
      daysOptions.push({ startDate: fee.startDate, day })
  })

  daysOptions.sort((a, b) => {
    return daysOfWeek.indexOf(a.day) - daysOfWeek.indexOf(b.day)
  })

  const onUpdate = () => {
    const schedulesSorted = sortScheduling(watchSchedule('custom') as TCustomSchedule[])
    const entriesSorted = sortScheduling(
      (watchEntries('registrationFees') as RegistrationFeesType[]).map((entry) => ({
        ...entry,
        children: sortScheduling(entry.children ?? []),
      }))
    )

    setValueSchedule('custom', schedulesSorted as TCustomSchedule[])
    setValueEntries('registrationFees', entriesSorted as RegistrationFeesType[])

    toastFunctions.success({ message: MESSAGES_CONST.EVENT_SCHEDULE_UPDATED })
  }

  const onAddCustomTime = () => {
    append({
      uuid: uuidv4(),
      name: '',
      startDate: '',
      location: '',
      tag: '',
      note: '',
      startTimeHours: '00',
      startTimeMinutes: '00',
      startTimeFormat: 'AM',
    })
  }

  const saveFees = async (dataFees: IEventFees) => {
    const dataToSave = new EventFeesModel(dataFees).toFirestore()

    const publishedEventInDb = await EventReviewPublishService.getById(eventId)

    await FirestoreService.updateItem(COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME, eventId, {
      ...publishedEventInDb,
      EventFees: dataToSave,
    })

    await FirestoreService.updateItem(COLLECTIONS.EVENT_FEES.NAME, eventId, dataToSave)
  }

  const handleSaveScheduleBy = async (scheduleBy: 'class' | 'rider') => {
    try {
      await FirestoreService.updateItem(COLLECTIONS.EVENT_SCHEDULING.NAME, eventId, { scheduleBy })

      toastFunctions.success({ message: `Scheduling updated to ${scheduleBy}` })
    } catch (error) {
      console.error('Failed to update scheduleBy:', error)
    }
  }

  const onSelected = (id: string) => {
    if (selectedIds.includes(id)) {
      const updated = selectedIds.filter((item) => item !== id)
      setSelectedIds([...updated])
    } else {
      setSelectedIds([...selectedIds, id])
    }
  }

  const onSaveBatch = async () => {
    try {
      setBatchLoading(true)
      const registrationFees =
        watchEntries('registrationFees')?.map((entry) => {
          if (selectedIds.includes(entry.uuid)) {
            return {
              ...entry,
              location: batchValues.facility || entry.location,
              startDate: batchValues.startDate || entry.startDate || '',
              tag: batchValues.tag || entry.tag || '',
            }
          } else {
            let isUpdateParent = false
            const children = entry.children?.map((children) => {
              if (selectedIds.includes(children.uuid)) {
                isUpdateParent = true
                return {
                  ...children,
                  location: batchValues.facility || children.location,
                  startDate: batchValues.startDate || children.startDate || '',
                  tag: batchValues.tag || entry.tag || '',
                }
              } else {
                return children
              }
            })

            if (isUpdateParent) {
              return {
                ...entry,
                location: batchValues.facility || entry.location,
                startDate: batchValues.startDate || entry.startDate || '',
                tag: batchValues.tag || entry.tag || '',
                children,
              }
            } else {
              return { ...entry, children }
            }
          }
        }) || []

      const customSchedule =
        watchSchedule('custom')?.map((schedule) => {
          if (selectedIds.includes(schedule.uuid)) {
            return {
              ...schedule,
              location: batchValues.facility || schedule.location,
              startDate: batchValues.startDate || schedule.startDate || '',
              tag: batchValues.tag || schedule.tag || '',
            }
          } else {
            return schedule
          }
        }) || []

      setValueEntries('registrationFees', registrationFees)
      setValueSchedule('custom', customSchedule)

      const publishedEventInDb = await EventReviewPublishService.getById(eventId)

      await FirestoreService.updateItem(COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME, eventId, {
        EventFees: {
          ...publishedEventInDb.EventFees,
          registrationFees: registrationFees,
        },
        EventSchedule: {
          ...publishedEventInDb.EventSchedule,
          custom: customSchedule,
        },
      })

      await FirestoreService.updateItem(COLLECTIONS.EVENT_FEES.NAME, eventId, {
        registrationFees: registrationFees,
      })

      await FirestoreService.updateItem(COLLECTIONS.EVENT_SCHEDULING.NAME, eventId, {
        custom: customSchedule,
      })

      setIsBatchSchedule(false)
      setBatchValues({ facility: '', startDate: '', tag: '' })
      setSelectedIds([])
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setBatchLoading(false)
    }
  }

  const countChildEntries =
    watchEntries('registrationFees')?.flatMap((item) => item.children || []) || []

  const countEntries =
    [...(watchEntries('registrationFees') || []), ...countChildEntries]?.filter((field) => {
      return filteredSchedule({ searchValue, filters, field, registrationByDay })
    }) || []

  const filteredCustomSchedule =
    watchSchedule('custom')?.filter((field) =>
      filteredSchedule({ searchValue, filters, field, registrationByDay })
    ) || []

  const scheduleIds = [
    ...(watchEntries('registrationFees') ?? []).flatMap((item) =>
      item?.children?.length ? item.children.map((c) => c.uuid) : [item.uuid]
    ),
    ...(watchSchedule('custom') ?? []).map((item) => item.uuid),
  ]

  return (
    <div className="pr-5 md:pr-0">
      <EventSchedulingFormComponentNoteModal
        show={noteModalShow}
        onHide={() => setNoteModalShow(false)}
      />

      <EventFormHeaderComponent
        title="Scheduling"
        description="Add facilities and then build your show schedule"
      >
        <EventFormFooterCommonComponent
          eventTab={eventTab}
          nextEventTab={nextEventTab}
          onNext={async (e, publishEvent, setLoading) => {
            await handleSubmitEntries(async (dataFees) => {
              try {
                setLoading(true)
                await saveFees(dataFees)
                await handleSubmitSchedule(async (dataSchedule) => {
                  await publishEvent({
                    dataToSave: dataSchedule,
                    tabName: 'EventSchedule',
                    validFormHandler: onValid,
                  })
                }, onInvalid)(e)
                setLoading(false)
              } catch (error) {
                setLoading(false)
                console.error(error, 'error')
              }
            }, onInvalid)(e).then(async () => {})
          }}
          onSaveAndExit={async (e, publishEvent, setSaveAndExitLoading) => {
            await handleSubmitEntries(async (dataFees) => {
              try {
                setSaveAndExitLoading(true)
                await saveFees(dataFees)
                await handleSubmitSchedule(
                  (dataSchedule) =>
                    publishEvent({
                      dataToSave: dataSchedule,
                      onSaveAndExit: true,
                      tabName: 'EventSchedule',
                      validFormHandler: onValidAndExit,
                    }),
                  onInvalidAndExit
                )(e)
                setSaveAndExitLoading(false)
              } catch (error) {
                setSaveAndExitLoading(false)
                console.error(error, 'error')
              }
            }, onInvalidAndExit)(e)
          }}
        />
      </EventFormHeaderComponent>
      <Facilities
        watch={watchSchedule}
        handleModal={handleModal}
        facilities={facilities}
        update={update}
        fields={fields}
        setLocationOptions={setLocationOptions}
        errors={errorsSchedule}
        setValue={setValueSchedule}
        register={registerSchedule}
        handleSaveScheduleBy={handleSaveScheduleBy}
      />
      <div className="flex flex-wrap gap-4 items-center justify-between mt-5 py-5 border-t border-solid border-[#e4e4e440]">
        <div className=" flex flex-col min-w-[290px]">
          <p className="text-lg font-bold text-SeabiscuitDark200ThemeColor whitespace-nowrap">
            {scheduleBy === 'rider' ? 'Schedule by rider' : 'Schedule by class'}
          </p>
          <div className="text-sm text-SeabiscuitDark200ThemeColor/50 whitespace-nowrap">
            {scheduleBy === 'rider'
              ? 'Select the day and facility per class'
              : 'Add times to riders and build a schedule'}
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-4">
          <p className="text-SeabiscuitDark200ThemeColor/50">
            {countEntries.length + filteredCustomSchedule.length} items
          </p>
          <div className="w-full sm:w-auto flex flex-wrap gap-4 bg-SeabiscuitGrayThemeColor rounded-md py-4 sm:py-1.5 px-4 sm:divide-x sm:divide-SeabiscuitLightThemeColorD3 sm:divide-solid">
            <div className="w-full sm:w-[160px]">
              <Select
                isDisabled={locationOptions?.[0]?.label?.length <= 0}
                className="p-0 bg-transparent border-none transition-all hover:opacity-70"
                classNamePrefix="entries-select"
                isClearable={false}
                isSearchable={true}
                placeholder="Facility"
                value={
                  filters.facility ? { label: filters.facility, value: filters.facility } : null
                }
                options={locationOptions}
                onChange={(newValue) => {
                  if (newValue) setFilters({ ...filters, facility: newValue.value })
                }}
                menuPlacement="auto"
                menuPortalTarget={document.body}
              />
            </div>
            <div className="w-full sm:w-[140px] sm:pl-4">
              <Select
                isDisabled={locationOptions?.[0]?.label?.length <= 0}
                className="p-0 bg-transparent border-none transition-all hover:opacity-70"
                classNamePrefix="entries-select"
                isClearable={false}
                isSearchable={true}
                placeholder="Day"
                value={filters.day ? { label: filters.day, value: filters.day } : null}
                options={
                  daysOptions
                    ? daysOptions.map((date) => ({ label: date.day, value: date.day }))
                    : []
                }
                onChange={(newValue) => {
                  if (newValue) setFilters({ ...filters, day: newValue.value })
                }}
                menuPlacement="auto"
                menuPortalTarget={document.body}
              />
            </div>
            <div className="w-full sm:w-[160px] sm:pl-4">
              <Select
                className="p-0 bg-transparent border-none transition-all hover:opacity-70"
                classNamePrefix="entries-select"
                isSearchable
                placeholder="Classes"
                value={filters.classes ? { label: filters.classes, value: filters.classes } : null}
                options={classOptions}
                onChange={(newValue) => {
                  if (newValue) setFilters({ ...filters, classes: newValue.value })
                }}
                menuPlacement="auto"
                menuPortalTarget={document.body}
              />
            </div>
            {(filters.facility || filters.day || filters.classes) && (
              <button
                className="sm:pl-4 text-[16px] text-SeabiscuitDark200ThemeColor hover:text-SeabiscuitDark200ThemeColor/50"
                onClick={() => setFilters({ facility: '', day: '', classes: '' })}
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="relative lg:w-[25%]">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-4">
            <IconSearch />
          </div>
          <input
            type="text"
            placeholder="Search class name"
            value={searchValue}
            className="pl-8 w-full max-w-[400px] border-t-0 border-l-0 border-r-0 border-b-SeabiscuitLightThemeColorD3"
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
        {isBatchSchedule ? (
          <div className="flex max-w-full justify-center flex-[1_0_auto] flex-wrap items-center gap-2.5">
            <div className="flex w-full sm:w-auto flex-wrap items-center justify-center gap-2.5 mx-auto">
              <div className="w-full sm:w-[180px]">
                <Select
                  isSearchable
                  isDisabled={locationOptions?.[0]?.label?.length <= 0}
                  className="w-full transition-all hover:opacity-70"
                  placeholder="Facility"
                  value={
                    batchValues.facility
                      ? { label: batchValues.facility, value: batchValues.facility }
                      : null
                  }
                  options={locationOptions}
                  onChange={(newValue) => {
                    if (newValue) setBatchValues({ ...batchValues, facility: newValue.value })
                  }}
                  styles={customSelectActiveStyles(batchValues.facility)}
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="w-full sm:w-[160px]">
                <CustomDatePicker
                  className={cn(
                    'border p-2.5 !rounded-lg border-solid w-full bg-transparent transition-all hover:opacity-70',
                    batchValues.startDate
                      ? 'border-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5'
                      : 'border-SeabiscuitGray500ThemeColor text-SeabiscuitDark200ThemeColor'
                  )}
                  onChange={(date) => setBatchValues({ ...batchValues, startDate: date })}
                  selected={getReactPickerDate(batchValues.startDate)}
                  peekNextMonth
                  dropdownMode="select"
                  showMonthDropdown
                  minDate={new Date()}
                  showYearDropdown
                  placeholderText="Day"
                  dateFormat="EEEE"
                  showArrow
                />
              </div>
              {scheduleBy === 'class' && (
                <div className="w-full sm:w-[180px]">
                  <Select
                    className="w-full transition-all hover:opacity-70"
                    placeholder="Tag"
                    value={TAG_OPTIONS.find((tag) => tag.value === batchValues.tag) || null}
                    options={TAG_OPTIONS}
                    onChange={(newValue) => {
                      if (newValue) setBatchValues({ ...batchValues, tag: newValue.value })
                    }}
                    styles={customSelectActiveStyles(batchValues.tag)}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2 divide-x divide-SeabiscuitDark200ThemeColor/50 divide-solid leading-none">
              <button
                disabled={batchLoading}
                className="underline text-SeabiscuitDark200ThemeColor/50 hover:text-SeabiscuitDark200ThemeColor transition-all"
                onClick={() => {
                  setIsBatchSchedule(false)
                  setBatchValues({ facility: '', startDate: '', tag: '' })
                  setSelectedIds([])
                }}
              >
                Cancel
              </button>
              <button
                disabled={batchLoading}
                className={cn(
                  'flex gap-1 items-center underline pl-2  transition-all',
                  batchValues.startDate || batchValues.facility || batchValues.tag
                    ? 'text-SeabiscuitMainThemeColor hover:text-SeabiscuitMainThemeColorDark'
                    : 'text-SeabiscuitDark200ThemeColor/50 hover:text-SeabiscuitDark200ThemeColor'
                )}
                onClick={onSaveBatch}
              >
                Save Changes
                {batchLoading && <Loader2Icon className="w-4 animate-spin" />}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-2 divide-x divide-SeabiscuitDark200ThemeColor/50 divide-solid underline text-SeabiscuitDark200ThemeColor/50 leading-none">
            <button
              className="hover:text-SeabiscuitDark200ThemeColor transition-all"
              onClick={() => {
                setIsBatchSchedule(true)
                setSelectedIds([])
              }}
            >
              Batch schedule
            </button>
            <Tooltip placement="top" arrow title={<h4 className="tooltip_title">Show preview</h4>}>
              <button
                className="pl-2 hover:text-SeabiscuitDark200ThemeColor transition-all"
                onClick={handleSchedule}
              >
                Preview
              </button>
            </Tooltip>
            <Tooltip placement="top" arrow title={<h4 className="tooltip_title">Sort by time</h4>}>
              <button
                className="pl-2 hover:text-SeabiscuitDark200ThemeColor transition-all"
                onClick={onUpdate}
              >
                Refresh
              </button>
            </Tooltip>
          </div>
        )}
      </div>

      <div id="fieldContainer" className="flex flex-col w-full text-xs wrapper-schedule">
        {isBatchSchedule && (
          <button
            className="ml-auto mt-4 underline text-SeabiscuitDark200ThemeColor/50 hover:text-SeabiscuitDark200ThemeColor transition-all"
            onClick={() => {
              if (selectedIds.length === scheduleIds.length) setSelectedIds([])
              else setSelectedIds(scheduleIds)
            }}
          >
            {selectedIds.length === scheduleIds.length ? 'Unselect All' : 'Select All'}
          </button>
        )}
        <EntriesSchedules
          handleModal={handleModal}
          trigger={triggerEntries}
          register={registerEntries}
          setValue={setValueEntries}
          control={controlEntries}
          watch={watchEntries}
          reset={resetEntries}
          errors={errorsEntries}
          locationOptions={locationOptions}
          filters={filters}
          searchValue={searchValue}
          scheduleBy={scheduleBy}
          selectedIds={selectedIds}
          onSelected={onSelected}
          isBatchSchedule={isBatchSchedule}
          registrationByDay={registrationByDay}
        />
        <CustomSchedules
          handleModal={handleModal}
          register={registerSchedule}
          control={controlSchedule}
          watch={watchSchedule}
          trigger={triggerSchedule}
          setValue={setValueSchedule}
          reset={resetSchedule}
          errors={errorsSchedule}
          locationOptions={locationOptions}
          remove={remove}
          filters={filters}
          searchValue={searchValue}
          scheduleBy={scheduleBy}
          selectedIds={selectedIds}
          onSelected={onSelected}
          isBatchSchedule={isBatchSchedule}
          registrationByDay={registrationByDay}
        />
      </div>
      <div className="mt-2 flex gap-4 items-center justify-between">
        <button
          className="text-SeabiscuitDark200ThemeColor/50 flex items-center gap-2 text-sm group"
          onClick={onAddCustomTime}
        >
          <AddCircleOutline className="!w-5" />
          <span className="underline group-hover:no-underline">Add custom time</span>
        </button>

        {watchSchedule('custom')!.length > 0 ? (
          <button
            onClick={() => remove(clearAllArr(watchSchedule('custom')!.length, 0))}
            className="text-SeabiscuitDark200ThemeColor/50 text-sm underline hover:no-underline"
          >
            Clear All
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default SchedulingScreenEventSchedulingFormComponent
